import { useMemo } from "react";
import { useLocale } from "@acto/web-common/locales";
import { Note } from "@acto/taskpane/core/domain";

export interface CompletedAppointment {
  id: string;
  date: string;
  title: string;
  clientExternalReference: string | null;
  todoCount: number;
  description: string;
  clientId: string;
  clientAccessible: boolean;
  noteId: string | null;
}

export interface CompletedAppointmentsData {
  entityName: string;
  dateRange: string;
  completedCount: number;
  appointments: CompletedAppointment[];
}

// Currently necessary because team page uses meetings while customer page uses recaps (notes) to populate the same component
export function useCustomerRecapsData(
  notes: Note[] | undefined,
  customerName: string,
): CompletedAppointmentsData | null {
  const { locale } = useLocale();

  return useMemo(() => {
    if (!notes) return null;

    const appointments: CompletedAppointment[] = notes.map((note) => {
      // Create description from summary or content
      const description =
        note.summary || (note.content.length > 150 ? note.content.slice(0, 150) + "..." : note.content);

      return {
        id: note.id,
        date: note.createdAt.locale(locale).format("D MMM"),
        title: note.title || customerName,
        clientExternalReference: null,
        todoCount: note.openTodosCount ?? 0,
        description,
        clientId: note.clientId,
        clientAccessible: true,
        noteId: note.id,
      };
    });

    return {
      entityName: customerName,
      dateRange: "",
      completedCount: appointments.length,
      appointments,
    };
  }, [notes, customerName, locale]);
}
